import logger from "logger";

export interface SessionMetrics {
  totalRequests: number;
  cacheHits: number;
  cacheMisses: number;
  errors: number;
  averageResponseTime: number;
  lastRequestTime: Date | null;
  requestsInLastMinute: number;
  requestsInLastHour: number;
}

export interface SessionRequestLog {
  timestamp: Date;
  type: "cache_hit" | "cache_miss" | "error" | "refresh";
  responseTime?: number;
  error?: string;
  cacheAge?: number;
  userId?: string;
  component?: string;
}

export interface SessionPerformanceAlert {
  type: "excessive_requests" | "high_error_rate" | "cache_inefficiency";
  message: string;
  timestamp: Date;
  metrics: Partial<SessionMetrics>;
}

class SessionMonitoringService {
  private metrics: SessionMetrics = {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    errors: 0,
    averageResponseTime: 0,
    lastRequestTime: null,
    requestsInLastMinute: 0,
    requestsInLastHour: 0,
  };

  private requestLog: SessionRequestLog[] = [];
  private responseTimes: number[] = [];
  private alertCallbacks: ((alert: SessionPerformanceAlert) => void)[] = [];

  // Configuration
  private readonly MAX_LOG_ENTRIES = 1000;
  private readonly EXCESSIVE_REQUESTS_THRESHOLD = 10; // per minute
  private readonly HIGH_ERROR_RATE_THRESHOLD = 0.1; // 10%
  private readonly CACHE_EFFICIENCY_THRESHOLD = 0.8; // 80% hit rate

  /**
   * Log a session request with performance metrics
   */
  logRequest(log: Omit<SessionRequestLog, "timestamp">): void {
    const entry: SessionRequestLog = {
      ...log,
      timestamp: new Date(),
    };

    // Add to log
    this.requestLog.push(entry);

    // Maintain log size
    if (this.requestLog.length > this.MAX_LOG_ENTRIES) {
      this.requestLog.shift();
    }

    // Update metrics
    this.updateMetrics(entry);

    // Check for alerts
    this.checkForAlerts();

    // Log based on detail level
    this.logWithAppropriateLevel(entry);
  }

  /**
   * Log cache hit
   */
  logCacheHit(cacheAge: number, component?: string, userId?: string): void {
    this.logRequest({
      type: "cache_hit",
      cacheAge,
      component,
      userId,
      responseTime: 1, // Cache hits are essentially instant
    });
  }

  /**
   * Log cache miss with response time
   */
  logCacheMiss(
    responseTime: number,
    component?: string,
    userId?: string,
  ): void {
    this.logRequest({
      type: "cache_miss",
      responseTime,
      component,
      userId,
    });
  }

  /**
   * Log session error
   */
  logError(error: Error, component?: string, userId?: string): void {
    this.logRequest({
      type: "error",
      error: error.message,
      component,
      userId,
    });
  }

  /**
   * Log session refresh
   */
  logRefresh(responseTime: number, component?: string, userId?: string): void {
    this.logRequest({
      type: "refresh",
      responseTime,
      component,
      userId,
    });
  }

  /**
   * Get current performance metrics
   */
  getMetrics(): SessionMetrics {
    return { ...this.metrics };
  }

  /**
   * Get recent request logs
   */
  getRecentLogs(limit = 100): SessionRequestLog[] {
    return this.requestLog.slice(-limit);
  }

  /**
   * Get cache hit rate
   */
  getCacheHitRate(): number {
    const total = this.metrics.cacheHits + this.metrics.cacheMisses;
    return total > 0 ? this.metrics.cacheHits / total : 0;
  }

  /**
   * Get error rate
   */
  getErrorRate(): number {
    return this.metrics.totalRequests > 0
      ? this.metrics.errors / this.metrics.totalRequests
      : 0;
  }

  /**
   * Subscribe to performance alerts
   */
  onAlert(callback: (alert: SessionPerformanceAlert) => void): () => void {
    this.alertCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.alertCallbacks.indexOf(callback);
      if (index > -1) {
        this.alertCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Reset metrics (useful for testing)
   */
  reset(): void {
    this.metrics = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      errors: 0,
      averageResponseTime: 0,
      lastRequestTime: null,
      requestsInLastMinute: 0,
      requestsInLastHour: 0,
    };
    this.requestLog = [];
    this.responseTimes = [];
  }

  /**
   * Get debugging information
   */
  getDebugInfo(): {
    metrics: SessionMetrics;
    recentLogs: SessionRequestLog[];
    cacheHitRate: number;
    errorRate: number;
    requestPattern: {
      lastMinute: number;
      lastHour: number;
      peakRequestsPerMinute: number;
    };
  } {
    return {
      metrics: this.getMetrics(),
      recentLogs: this.getRecentLogs(50),
      cacheHitRate: this.getCacheHitRate(),
      errorRate: this.getErrorRate(),
      requestPattern: {
        lastMinute: this.metrics.requestsInLastMinute,
        lastHour: this.metrics.requestsInLastHour,
        peakRequestsPerMinute: this.calculatePeakRequestsPerMinute(),
      },
    };
  }

  private updateMetrics(entry: SessionRequestLog): void {
    this.metrics.totalRequests++;
    this.metrics.lastRequestTime = entry.timestamp;

    switch (entry.type) {
      case "cache_hit":
        this.metrics.cacheHits++;
        break;
      case "cache_miss":
      case "refresh":
        this.metrics.cacheMisses++;
        break;
      case "error":
        this.metrics.errors++;
        break;
    }

    // Update response time average
    if (entry.responseTime !== undefined) {
      this.responseTimes.push(entry.responseTime);
      // Keep only last 100 response times for average calculation
      if (this.responseTimes.length > 100) {
        this.responseTimes.shift();
      }
      this.metrics.averageResponseTime =
        this.responseTimes.reduce((sum, time) => sum + time, 0) /
        this.responseTimes.length;
    }

    // Update time-based counters
    this.updateTimeBasedCounters();
  }

  private updateTimeBasedCounters(): void {
    const now = Date.now();
    const oneMinuteAgo = now - 60 * 1000;
    const oneHourAgo = now - 60 * 60 * 1000;

    this.metrics.requestsInLastMinute = this.requestLog.filter(
      (log) => log.timestamp.getTime() > oneMinuteAgo,
    ).length;

    this.metrics.requestsInLastHour = this.requestLog.filter(
      (log) => log.timestamp.getTime() > oneHourAgo,
    ).length;
  }

  private calculatePeakRequestsPerMinute(): number {
    const now = Date.now();
    let maxRequests = 0;

    // Check each minute in the last hour
    for (let i = 0; i < 60; i++) {
      const minuteStart = now - (i + 1) * 60 * 1000;
      const minuteEnd = now - i * 60 * 1000;

      const requestsInMinute = this.requestLog.filter(
        (log) =>
          log.timestamp.getTime() >= minuteStart &&
          log.timestamp.getTime() < minuteEnd,
      ).length;

      maxRequests = Math.max(maxRequests, requestsInMinute);
    }

    return maxRequests;
  }

  private checkForAlerts(): void {
    // Check for excessive requests
    if (this.metrics.requestsInLastMinute > this.EXCESSIVE_REQUESTS_THRESHOLD) {
      this.emitAlert({
        type: "excessive_requests",
        message: `Excessive session requests detected: ${this.metrics.requestsInLastMinute} requests in the last minute`,
        timestamp: new Date(),
        metrics: {
          requestsInLastMinute: this.metrics.requestsInLastMinute,
          totalRequests: this.metrics.totalRequests,
        },
      });
    }

    // Check for high error rate
    const errorRate = this.getErrorRate();
    if (
      errorRate > this.HIGH_ERROR_RATE_THRESHOLD &&
      this.metrics.totalRequests > 10
    ) {
      this.emitAlert({
        type: "high_error_rate",
        message: `High session error rate detected: ${(errorRate * 100).toFixed(1)}%`,
        timestamp: new Date(),
        metrics: {
          errors: this.metrics.errors,
          totalRequests: this.metrics.totalRequests,
        },
      });
    }

    // Check for cache inefficiency
    const cacheHitRate = this.getCacheHitRate();
    const totalCacheableRequests =
      this.metrics.cacheHits + this.metrics.cacheMisses;
    if (
      cacheHitRate < this.CACHE_EFFICIENCY_THRESHOLD &&
      totalCacheableRequests > 10
    ) {
      this.emitAlert({
        type: "cache_inefficiency",
        message: `Low cache hit rate detected: ${(cacheHitRate * 100).toFixed(1)}%`,
        timestamp: new Date(),
        metrics: {
          cacheHits: this.metrics.cacheHits,
          cacheMisses: this.metrics.cacheMisses,
        },
      });
    }
  }

  private emitAlert(alert: SessionPerformanceAlert): void {
    // Log the alert
    logger.warn("Session Performance Alert", alert);

    // Notify subscribers
    this.alertCallbacks.forEach((callback) => {
      try {
        callback(alert);
      } catch (error) {
        logger.error("Error in session monitoring alert callback:", error);
      }
    });
  }

  private logWithAppropriateLevel(entry: SessionRequestLog): void {
    const baseMessage = `Session ${entry.type}`;
    const details = {
      component: entry.component,
      userId: entry.userId,
      responseTime: entry.responseTime,
      cacheAge: entry.cacheAge,
      error: entry.error,
    };

    switch (entry.type) {
      case "cache_hit":
        // Debug level for cache hits (most common)
        logger.debug(baseMessage, details);
        break;
      case "cache_miss":
        // Info level for cache misses (important for optimization)
        logger.info(baseMessage, details);
        break;
      case "refresh":
        // Info level for manual refreshes
        logger.info(baseMessage, details);
        break;
      case "error":
        // Error level for errors
        logger.error(baseMessage, details);
        break;
    }
  }
}

// Export singleton instance
export const sessionMonitoringService = new SessionMonitoringService();
